import React from "react";

import Form from "antd/es/form";
import { FormInstance } from "antd/es/form/hooks/useForm";
import Radio from "antd/es/radio";
import Space from "antd/es/space";
import type Calendar from "nylas/lib/models/calendar";
import { IntlShape } from "react-intl";

import Checkbox from "@app/component/input/Checkbox";
import TextWithInfo from "@app/component/typography/TextWithInfo";
import InfoField from "@app/scene/settings/component/CalendarSync/component/InfoField";
import NylasForm from "@app/scene/settings/type/NylasForm";
import NylasInfo from "@app/types/nylas/NylasInfo";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";

import ActivityTypes from "../ActivityTypes";
import SyncDirection from "../SyncDirection";
import SyncDirectionDescription from "../SyncDirection/SyncDirectionDescription";

import styles from "./Fields.module.scss";

interface Props {
  analyticIssuer?: AnalyticsService;
  calendars: Calendar[];
  form: FormInstance<NylasForm>;
  intl: IntlShape;
  nylasInfo: NylasInfo;
  onHide: () => void;
}

const Fields: React.FC<Props> = ({ analyticIssuer, calendars, form, intl, nylasInfo, onHide }) => {
  return (
    <div className={styles.fields}>
      <InfoField hideIfFinished nylasInfo={nylasInfo} onDelete={onHide} />
      <div>
        <p>
          {intl.formatMessage({
            id: "settings.calendar.createEditPane.calendar",
            defaultMessage: "Select calendar to sync",
            description: "Settings calendar create/edit pane - Select calendar to sync",
          })}
        </p>
        <Form.Item name="calendarId">
          <Radio.Group className={styles.values}>
            <Space direction="vertical">
              {calendars.map(({ id, name }) => (
                <Radio key={id} value={id}>
                  {name}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </Form.Item>
      </div>
      <div>
        <p>
          {intl.formatMessage({
            id: "settings.calendar.createEditPane.syncDirection.label",
            defaultMessage: "Sync direction",
            description: "Settings calendar create/edit pane - sync direction - Sync direction",
          })}
        </p>
        <div className={styles.syncOptions}>
          <div className={styles.syncOptionsList}>
            <Form.Item name="syncOptions" noStyle>
              <SyncDirection analyticIssuer={analyticIssuer} />
            </Form.Item>
          </div>
          <div>
            <Form.Item
              noStyle
              shouldUpdate={(before, after) => before.syncOptions !== after.syncOptions}
            >
              {() => (
                <Form.Item name="calendarCrmActivityType" noStyle>
                  <SyncDirectionDescription form={form} />
                </Form.Item>
              )}
            </Form.Item>
          </div>
        </div>
      </div>
      <Form.Item name="nylasCrmActivityTypes">
        <ActivityTypes />
      </Form.Item>
      <div>
        <p>
          {intl.formatMessage({
            id: "settings.calendar.createEditPane.activitiesToSync",
            defaultMessage: "Activities to sync",
            description: "Settings calendar create/edit pane - Activities to sync",
          })}
        </p>
        <Form.Item
          className={styles.values}
          name={["syncPreference", "assigned"]}
          valuePropName="checked"
        >
          <Checkbox>
            {intl.formatMessage({
              id: "settings.calendar.createEditPane.activitiesToSync.assigned",
              defaultMessage: "Activities assigned to me",
              description: "Settings calendar create/edit pane - Activities assigned to me",
            })}
          </Checkbox>
        </Form.Item>
        <Form.Item
          className={styles.values}
          name={["syncPreference", "owned"]}
          valuePropName="checked"
        >
          <Checkbox>
            {intl.formatMessage({
              id: "settings.calendar.createEditPane.activitiesToSync.owned",
              defaultMessage: "Activities owned by me",
              description: "Settings calendar create/edit pane - Activities owned by me",
            })}
          </Checkbox>
        </Form.Item>
      </div>
      <div>
        <p>
          {intl.formatMessage({
            id: "settings.calendar.createEditPane.additionalPreferences",
            defaultMessage: "Additional preferences",
            description: "Settings calendar create/edit pane - Additional preferences",
          })}
        </p>
        <Form.Item className={styles.values} name="recurActivitySync" valuePropName="checked">
          <Checkbox>
            {intl.formatMessage({
              id: "settings.calendar.createEditPane.activitiesToSync.recurring",
              defaultMessage: "Sync recurring activities",
              description: "Settings calendar create/edit pane - Sync recurring activities",
            })}
          </Checkbox>
        </Form.Item>
        <Form.Item className={styles.values} name="syncNotes" valuePropName="checked">
          <Checkbox>
            <TextWithInfo
              className={styles.icon}
              info={intl.formatMessage({
                id: "settings.calendar.createEditPane.activitiesToSync.notes.description",
                defaultMessage:
                  "If enabled, invitees on your calendar may be able to view notes left on your Map My Customers activity",
                description:
                  "Settings calendar create/edit pane - Sync activities notes description",
              })}
            >
              {intl.formatMessage({
                id: "settings.calendar.createEditPane.activitiesToSync.notes",
                defaultMessage: "Sync activity notes",
                description: "Settings calendar create/edit pane - Sync activities notes",
              })}
            </TextWithInfo>
          </Checkbox>
        </Form.Item>
      </div>
    </div>
  );
};

export default Fields;
